import { FormEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { ApiError, fetchVenuePage, submitSignup, type OfferInfo, type VenueInfo } from '../api';

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format raw digit input as (555) 123-4567. */
function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** Convert formatted phone to E.164 (+1XXXXXXXXXX). */
function toE164(phone: string): string {
  return `+1${phone.replace(/\D/g, '')}`;
}

/** ISO date string (YYYY-MM-DD) for today minus 21 years — the latest allowed birthdate. */
function maxBirthdateValue(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 21);
  return d.toISOString().split('T')[0];
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ── Types ─────────────────────────────────────────────────────────────────────

type VenueStatus = 'loading' | 'ready' | 'not_found' | 'not_accepting' | 'error';
type SubmitStatus = 'idle' | 'submitting' | 'pending' | 'error';

interface FormErrors {
  phone?: string;
  email?: string;
  birthdate?: string;
  optInConsent?: string;
  form?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function SignupPage() {
  const { venueShortName = '' } = useParams<{ venueShortName: string }>();
  const brandingRef = useRef<HTMLDivElement>(null);

  // Venue state
  const [venueStatus, setVenueStatus] = useState<VenueStatus>('loading');
  const [venue, setVenue] = useState<VenueInfo | null>(null);
  const [offer, setOffer] = useState<OfferInfo | null>(null);

  // Submission state
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');
  const [submitError, setSubmitError] = useState('');

  // Form field state
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [optInConsent, setOptInConsent] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  // ── Venue fetch ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!venueShortName) {
      setVenueStatus('not_found');
      return;
    }
    let cancelled = false;
    fetchVenuePage(venueShortName)
      .then((data) => {
        if (cancelled) return;
        if (data) {
          setVenue(data.venue);
          setOffer(data.offer);
          console.debug('[barbridge] venue loaded', data.venue);
        } else {
          console.warn('[barbridge] fetchVenuePage returned null — no branding will be shown');
        }
        setVenueStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setVenueStatus('not_found');
            return;
          }
          if (err.status === 422) {
            setVenueStatus('not_accepting');
            return;
          }
        }
        // Any other failure: show the form without branding rather than blocking.
        setVenueStatus('ready');
      });
    return () => {
      cancelled = true;
    };
  }, [venueShortName]);

  // Apply venue brand colors via CSS custom properties.
  useEffect(() => {
    const el = brandingRef.current;
    if (!el || !venue) return;
    if (venue.background_color) el.style.setProperty('--venue-bg', venue.background_color);
    if (venue.foreground_color) el.style.setProperty('--venue-fg', venue.foreground_color);
  }, [venue]);

  // ── Validation ──────────────────────────────────────────────────────────────

  function validate(): FormErrors {
    const errs: FormErrors = {};
    const digits = phone.replace(/\D/g, '');
    if (!digits) errs.phone = 'Phone number is required.';
    else if (digits.length !== 10) errs.phone = 'Enter a 10-digit US phone number.';
    if (email.trim() && !isValidEmail(email)) errs.email = 'Enter a valid email address.';
    if (!birthdate) errs.birthdate = 'Date of birth is required.';
    else if (birthdate > maxBirthdateValue())
      errs.birthdate = 'You must be 21 or older to sign up.';
    if (!optInConsent) errs.optInConsent = 'You must agree to receive text messages to sign up.';
    return errs;
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitStatus('submitting');
    setSubmitError('');
    try {
      const payload: Parameters<typeof submitSignup>[1] = {
        phone: toE164(phone),
        birthdate,
        delivery_consent: optInConsent,
        marketing_consent: optInConsent,
      };
      if (firstName.trim()) payload.first_name = firstName.trim();
      if (lastName.trim()) payload.last_name = lastName.trim();
      if (email.trim()) payload.email = email.trim().toLowerCase();
      await submitSignup(venueShortName, payload);
      setSubmitStatus('pending');
    } catch (err: unknown) {
      let msg = 'Something went wrong. Please try again.';
      if (err instanceof ApiError) {
        if (err.status === 429) msg = 'Too many attempts. Please wait a few minutes and try again.';
        else if (err.status === 400 && err.message) msg = err.message;
        else if (err.status === 404) msg = 'This venue could not be found.';
        else if (err.status === 422) msg = 'This venue is not currently accepting signups.';
      }
      setSubmitError(msg);
      setSubmitStatus('error');
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (venueStatus === 'loading') {
    return (
      <main className="container" aria-busy="true">
        <div className="spinner" role="status">
          <span className="sr-only">Loading…</span>
        </div>
      </main>
    );
  }

  if (venueStatus === 'not_found') {
    return (
      <main className="container">
        <h1>Venue not found</h1>
        <p>Check the QR code at your venue or ask the bartender for help.</p>
      </main>
    );
  }

  if (venueStatus === 'not_accepting') {
    return (
      <main className="container">
        <h1>Signups paused</h1>
        <p>This venue isn&apos;t accepting new signups right now. Check back soon.</p>
      </main>
    );
  }

  if (submitStatus === 'pending') {
    return (
      <main className="container holding-page">
        <h1>Check your phone</h1>
        <p>We sent you a link to claim your card. Tap it within 15 minutes.</p>
        <p className="text-muted">
          Didn&apos;t receive it?{' '}
          <button type="button" className="btn-link" onClick={() => setSubmitStatus('idle')}>
            Go back and try again.
          </button>
        </p>
      </main>
    );
  }

  const maxBirthdate = maxBirthdateValue();
  const venueName = venue?.name ?? venueShortName;

  return (
    <div ref={brandingRef} className="signup-page">
      {venue?.logo_url && (
        <header className="venue-header">
          <img src={venue.logo_url} alt={venueName} className="venue-logo" />
        </header>
      )}

      <main className="container">
        <h1>Welcome to {venueName}</h1>

        <p className="form-intro">Please complete the form below to receive this offer:</p>

        {offer && (
          <div className="offer-preview">
            <p className="offer-title">{offer.title}</p>
            <p className="offer-body">{offer.body}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate>
          {/* Phone */}
          <div className="field">
            <label htmlFor="phone" className="field-label">
              Mobile phone number*
            </label>
            <input
              id="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              className={`field-input${errors.phone ? ' field-input--error' : ''}`}
              value={phone}
              onChange={(e) => setPhone(formatPhone(e.target.value))}
              placeholder="(555) 123-4567"
              aria-describedby={errors.phone ? 'phone-error' : undefined}
              aria-invalid={errors.phone ? true : undefined}
            />
            {errors.phone && (
              <span id="phone-error" className="field-error" role="alert">
                {errors.phone}
              </span>
            )}
          </div>

          {/* Birthdate */}
          <div className="field">
            <label htmlFor="birthdate" className="field-label">
              Date of birth* <span className="field-hint">(must be 21 or older)</span>
            </label>
            <input
              id="birthdate"
              type="date"
              autoComplete="bday"
              className={`field-input${errors.birthdate ? ' field-input--error' : ''}`}
              value={birthdate}
              onChange={(e) => setBirthdate(e.target.value)}
              max={maxBirthdate}
              min="1900-01-01"
              aria-describedby={errors.birthdate ? 'birthdate-error' : undefined}
              aria-invalid={errors.birthdate ? true : undefined}
            />
            {errors.birthdate && (
              <span id="birthdate-error" className="field-error" role="alert">
                {errors.birthdate}
              </span>
            )}
          </div>

          {/* First name */}
          <div className="field">
            <label htmlFor="firstName" className="field-label">
              First name <span className="field-hint">(to receive personalized perks)</span>
            </label>
            <input
              id="firstName"
              type="text"
              autoComplete="given-name"
              className="field-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>

          {/* Last name */}
          <div className="field">
            <label htmlFor="lastName" className="field-label">
              Last name <span className="field-hint">(to receive personalized perks)</span>
            </label>
            <input
              id="lastName"
              type="text"
              autoComplete="family-name"
              className="field-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>

          {/* Email (optional) */}
          <div className="field">
            <label htmlFor="email" className="field-label">
              Email <span className="field-hint">(if you want to also receive offers via email)</span>
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              className={`field-input${errors.email ? ' field-input--error' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              aria-describedby={errors.email ? 'email-error' : undefined}
              aria-invalid={errors.email ? true : undefined}
            />
            {errors.email && (
              <span id="email-error" className="field-error" role="alert">
                {errors.email}
              </span>
            )}
          </div>

          <p className="required-note">*Required</p>

          {/* SMS opt-in consent (required) */}
          <div className="field checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={optInConsent}
                onChange={(e) => setOptInConsent(e.target.checked)}
                aria-describedby={errors.optInConsent ? 'optInConsent-error' : undefined}
                aria-invalid={errors.optInConsent ? true : undefined}
              />
              <span>
                I agree to receive an SMS text message from BarBridge on behalf of {venueName} with
                a link to download your card as well as (at most) weekly SMS offers. Message and
                data rates may apply. Reply STOP to opt out.{' '}
                <a href="https://www.barbridge.io/privacy.html" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                .
              </span>
            </label>
            {errors.optInConsent && (
              <span id="optInConsent-error" className="field-error" role="alert">
                {errors.optInConsent}
              </span>
            )}
          </div>

          {submitStatus === 'error' && (
            <div className="error-banner" role="alert">
              {submitError}
            </div>
          )}

          <button
            type="submit"
            className="btn btn-primary"
            disabled={submitStatus === 'submitting'}
          >
            {submitStatus === 'submitting' ? 'Sending…' : 'Get my card'}
          </button>
        </form>
      </main>
    </div>
  );
}
